import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarClock, CheckCircle2, XCircle } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import useSchedulingIntegration from '../../hooks/useSchedulingIntegration';
import { recordSiteVisitOutcome } from '../../services/api/implementation/impl';
import {
  ABORT_REASONS,
  OUTCOME_CHOICES,
  PENDING_SITE_VISITS_QUERY_KEY,
  buildOutcomePayload,
  defaultDaysAhead,
  defaultFollowUpInput,
  formatVisitTime,
  needsRebooking,
  toDateTimeLocal
} from './siteVisitOutcome';

const CHOICE_STYLES = {
  success: {
    icon: CheckCircle2,
    iconClass: 'text-green-600',
    selected: 'border-green-500 bg-green-50'
  },
  abort: { icon: XCircle, iconClass: 'text-red-500', selected: 'border-red-400 bg-red-50' },
  reschedule: {
    icon: CalendarClock,
    iconClass: 'text-indigo-500',
    selected: 'border-indigo-400 bg-indigo-50'
  }
};

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none';
const labelClass = 'mb-1 block text-sm font-medium text-gray-700';

/** The three outcome buttons, for rows that open the modal with one pre-selected. */
export function SiteVisitOutcomeButtons({ onPick }) {
  return (
    <>
      {OUTCOME_CHOICES.map((choice) => {
        const { icon: Icon, iconClass } = CHOICE_STYLES[choice.id];
        return (
          <button
            key={choice.id}
            type="button"
            title={choice.hint}
            onClick={() => onPick(choice.id)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50">
            <Icon className={`h-4 w-4 ${iconClass}`} />
            {choice.label}
          </button>
        );
      })}
    </>
  );
}

function RadioCard({ name, checked, onChange, label, hint }) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
        checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
      }`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1" />
      <span>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {hint ? <span className="block text-xs text-gray-500">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * Record what happened at a site visit: Visit done, Abort (with a reason) or
 * Reschedule. The backend moves the stage, closes or re-dates the follow-up
 * and, when asked, sends the lead the WhatsApp time picker.
 */
export default function SiteVisitOutcomeModal({
  leadId,
  leadName,
  visitAt,
  initialOutcome = null,
  onClose,
  onDone
}) {
  const queryClient = useQueryClient();
  const { canBookInApp: pickerAvailable } = useSchedulingIntegration();
  const [form, setForm] = useState(() => ({
    outcome: initialOutcome,
    abortReason: '',
    note: '',
    nextFollowUpAt: defaultFollowUpInput(defaultDaysAhead(initialOutcome)),
    rescheduleMode: 'whatsapp',
    rescheduleAt: defaultFollowUpInput(1),
    sendWhatsAppPicker: true
  }));

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const chooseOutcome = (outcome) =>
    update({ outcome, nextFollowUpAt: defaultFollowUpInput(defaultDaysAhead(outcome)) });

  const mutation = useMutation({
    mutationFn: (payload) => recordSiteVisitOutcome(leadId, payload),
    onSuccess: (res) => {
      const data = res?.data;
      toast.success(res?.message || 'Site visit outcome saved');
      if (data?.whatsapp?.sent && data.whatsapp.via === 'flow') {
        toast.success(
          `The lead hasn't messaged in 24 hours, so "${data.whatsapp.flowName}" was sent. Once they tap it, they get the time picker.`,
          { duration: 7000 }
        );
      } else if (data?.whatsapp?.sent) {
        toast.success('Time picker sent to the lead on WhatsApp');
      } else if (data?.whatsapp) {
        toast.error(
          `Saved, but the WhatsApp time picker was not sent: ${
            data.whatsapp.error || 'unknown error'
          }`
        );
      }
      (data?.warnings || []).forEach((warning) => toast(warning, { icon: '⚠️', duration: 8000 }));

      const id = Number(leadId);
      queryClient.invalidateQueries({ queryKey: ['leadById', id] });
      queryClient.invalidateQueries({ queryKey: ['getLogsByID', id] });
      queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', id] });
      queryClient.invalidateQueries({ queryKey: ['schedulingLeadMeetings'] });
      queryClient.invalidateQueries({ queryKey: PENDING_SITE_VISITS_QUERY_KEY });
      onDone?.(data);
      onClose();
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || 'Could not save the site visit outcome')
  });

  const handleSubmit = () => {
    const { payload, error } = buildOutcomePayload(form, { pickerAvailable });
    if (error) {
      toast.error(error);
      return;
    }
    mutation.mutate(payload);
  };

  const minDateTime = toDateTimeLocal(new Date());
  const rescheduleMode = pickerAvailable ? form.rescheduleMode : 'time';
  const closesLead = form.outcome === 'abort' && form.abortReason === 'not_interested';
  const showNextFollowUp = form.outcome === 'success' || needsRebooking(form);
  const visitLabel = formatVisitTime(visitAt);

  return (
    <Modal name="Site visit outcome" width={44} onClose={onClose}>
      <div className="space-y-5">
        {leadName || visitLabel ? (
          <p className="text-sm text-gray-600">
            {leadName ? <span className="font-semibold text-gray-900">{leadName}</span> : null}
            {leadName && visitLabel ? ' · ' : ''}
            {visitLabel ? `visit on ${visitLabel}` : ''}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {OUTCOME_CHOICES.map((choice) => {
            const { icon: Icon, iconClass, selected } = CHOICE_STYLES[choice.id];
            const isSelected = form.outcome === choice.id;
            return (
              <button
                key={choice.id}
                type="button"
                onClick={() => chooseOutcome(choice.id)}
                aria-pressed={isSelected}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                  isSelected ? selected : 'border-gray-200 hover:bg-gray-50'
                }`}>
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Icon className={`h-4 w-4 ${iconClass}`} />
                  {choice.label}
                </span>
                <span className="mt-1 block text-xs text-gray-500">{choice.hint}</span>
              </button>
            );
          })}
        </div>

        {form.outcome === 'abort' ? (
          <fieldset className="space-y-2">
            <legend className={labelClass}>What happened?</legend>
            {ABORT_REASONS.map((reason) => (
              <RadioCard
                key={reason.id}
                name="siteVisitAbortReason"
                checked={form.abortReason === reason.id}
                onChange={() => update({ abortReason: reason.id })}
                label={reason.label}
                hint={reason.hint}
              />
            ))}
          </fieldset>
        ) : null}

        {form.outcome === 'reschedule' ? (
          <div className="space-y-2">
            {pickerAvailable ? (
              <>
                <RadioCard
                  name="siteVisitRescheduleMode"
                  checked={rescheduleMode === 'whatsapp'}
                  onChange={() => update({ rescheduleMode: 'whatsapp' })}
                  label="Let the lead pick a new time on WhatsApp"
                  hint="Sends the lead the date and time picker."
                />
                <RadioCard
                  name="siteVisitRescheduleMode"
                  checked={rescheduleMode === 'time'}
                  onChange={() => update({ rescheduleMode: 'time' })}
                  label="I have agreed a new time with the lead"
                />
              </>
            ) : null}
            {rescheduleMode === 'time' ? (
              <label className="block">
                <span className={labelClass}>New visit date &amp; time</span>
                <input
                  type="datetime-local"
                  className={inputClass}
                  min={minDateTime}
                  value={form.rescheduleAt}
                  onChange={(e) => update({ rescheduleAt: e.target.value })}
                />
              </label>
            ) : null}
          </div>
        ) : null}

        {showNextFollowUp ? (
          <label className="block">
            <span className={labelClass}>Next follow-up</span>
            <input
              type="datetime-local"
              className={inputClass}
              min={minDateTime}
              value={form.nextFollowUpAt}
              onChange={(e) => update({ nextFollowUpAt: e.target.value })}
            />
          </label>
        ) : null}

        {needsRebooking(form) && pickerAvailable ? (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.sendWhatsAppPicker}
              onChange={(e) => update({ sendWhatsAppPicker: e.target.checked })}
            />
            Send the lead a WhatsApp message to pick a new time
          </label>
        ) : null}

        {form.outcome ? (
          <label className="block">
            <span className={labelClass}>
              {closesLead ? 'Reason — why not interested' : 'Note (optional)'}
            </span>
            <textarea
              rows={3}
              maxLength={1000}
              className={inputClass}
              value={form.note}
              onChange={(e) => update({ note: e.target.value })}
              placeholder={
                closesLead ? 'Budget, location, timeline, competitor...' : 'Anything worth noting...'
              }
            />
          </label>
        ) : null}

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-800 hover:bg-gray-50">
            Cancel
          </button>
          <Button
            displayType="primary"
            onClick={handleSubmit}
            disabled={!form.outcome || mutation.isPending}>
            Save outcome
          </Button>
        </div>
      </div>
    </Modal>
  );
}
